import { useCallback, useEffect, useState } from "react";
import AlbumDetail from "../components/albums/AlbumDetail";
import AlbumLightbox from "../components/albums/AlbumLightbox";
import AlbumsIndex from "../components/albums/AlbumsIndex";
import PageMeta from "../components/seo/PageMeta";
import { getAlbumById, ALBUMS } from "../data/albums";
import { buildBreadcrumbJsonLd, buildImageGalleryJsonLd, buildWebPageJsonLd } from "../lib/seo";
import "../styles/albums.css";

function AlbumsComingSoonOverlay() {
  return (
    <div className="albums-coming-soon-shell__overlay" role="status" aria-live="polite">
      <p className="albums-coming-soon-shell__heading">COMING SOON</p>
      <p className="albums-coming-soon-shell__note">
        Sorry, not enough time during the day but I am working on this.
      </p>
    </div>
  );
}

function AlbumsComingSoonShell({ children }) {
  return (
    <div className="albums-coming-soon-shell">
      {children}
      <AlbumsComingSoonOverlay />
    </div>
  );
}

function readAlbumIdFromHash() {
  const hash = window.location.hash.replace(/^#/, "").trim();
  return hash || null;
}

export default function AlbumsPage() {
  const [albumId, setAlbumId] = useState(readAlbumIdFromHash);
  const [lightbox, setLightbox] = useState(null);

  const album = albumId ? getAlbumById(albumId) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [albumId]);

  useEffect(() => {
    const onHashChange = () => {
      setAlbumId(readAlbumIdFromHash());
      setLightbox(null);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (albumId && !album) {
      window.history.replaceState(null, "", window.location.pathname);
      setAlbumId(null);
    }
  }, [albumId, album]);

  const openAlbum = useCallback((id) => {
    window.location.hash = id;
    setAlbumId(id);
    setLightbox(null);
  }, []);

  const backToAlbums = useCallback(() => {
    window.history.pushState(null, "", window.location.pathname);
    setAlbumId(null);
    setLightbox(null);
  }, []);

  const openLightbox = useCallback((list, index) => {
    setLightbox({ list, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox(null);
  }, []);

  const changeLightboxIndex = useCallback((index) => {
    setLightbox((current) => (current ? { ...current, index } : current));
  }, []);

  const albumCoverKey = album?.cover?.image || album?.photos?.[0]?.image;
  const albumPath = album ? `/albums#${album.id}` : "/albums";

  return (
    <AlbumsComingSoonShell>
      <PageMeta
        title={album ? `${album.title} — Album` : "Albums"}
        description={
          album
            ? `${album.subtitle} A curated photo album by CJ Jayamaha with ${album.count} images.`
            : "Curated photo albums by CJ Jayamaha — weddings, family sessions, retreats, and commissioned work."
        }
        path={albumPath}
        image={albumCoverKey || ALBUMS[0]?.cover?.image || "cjmeister-com-_CJ08407.jpg"}
        keywords={
          album
            ? [album.title, "photo album", "CJ Jayamaha", album.subtitle]
            : ["photo albums", "wedding photography", "portraits", "CJ Jayamaha"]
        }
        jsonLd={
          album
            ? [
                buildWebPageJsonLd({
                  title: album.title,
                  description: album.subtitle,
                  path: albumPath,
                  image: albumCoverKey,
                }),
                buildImageGalleryJsonLd({
                  title: album.title,
                  description: album.subtitle,
                  path: albumPath,
                  image: albumCoverKey,
                  collectionSize: album.count,
                }),
                buildBreadcrumbJsonLd([
                  { name: "Home", path: "/" },
                  { name: "Albums", path: "/albums" },
                  { name: album.title, path: albumPath },
                ]),
              ]
            : buildWebPageJsonLd({
                title: "Photo Albums",
                description:
                  "Curated photo albums by CJ Jayamaha for weddings, families, and commissioned work.",
                path: "/albums",
                image: ALBUMS[0]?.cover?.image || "cjmeister-com-_CJ08407.jpg",
              })
        }
      />
      {album ? (
        <AlbumDetail
          album={album}
          onBack={backToAlbums}
          onOpenLightbox={openLightbox}
        />
      ) : (
        <AlbumsIndex onOpenAlbum={openAlbum} />
      )}

      <AlbumLightbox
        lightbox={lightbox}
        onClose={closeLightbox}
        onChangeIndex={changeLightboxIndex}
      />
    </AlbumsComingSoonShell>
  );
}
